import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Card, CardContent, LinearProgress} from 'views/components';
import {Translate} from 'views/module';
import {Description, Psychology, Lightbulb, TaskAlt} from 'views/icons';

const FileLoadingSection = ({currentStep = 0}) => {
  const steps = [
    {
      icon: Description,
      label: 'Extracting text',
      key: 'extracting',
    },
    {
      icon: Psychology,
      label: 'Understanding experience',
      key: 'understanding',
    },
    {
      icon: Lightbulb,
      label: 'Detecting skills',
      key: 'detecting',
    },
    {
      icon: TaskAlt,
      label: 'Preparing your application',
      key: 'preparing',
    },
  ];

  return (
    <Box sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}>
      <Stack spacing={4} alignItems="center">
        {/* Header Section */}
        <Box textAlign="center">
          <Typography variant="h3" component="h1" gutterBottom>
            <Translate expression="caregiverInterview.Resume.Title" />
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{maxWidth: 600}}>
            <Translate expression="caregiverInterview.Resume.Subtitle" />
          </Typography>
        </Box>

        {/* Processing Card */}
        <Card sx={{width: '100%', maxWidth: 800, mt: 4}}>
          <CardContent sx={{p: 4}}>
            {/* Processing Header */}
            <Box textAlign="center" sx={{mb: 4}}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                <Translate expression="caregiverInterview.Resume.Processing.Title" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Translate expression="caregiverInterview.Resume.Processing.Subtitle" />
              </Typography>
            </Box>

            {/* Steps */}
            <Stack spacing={2} sx={{mb: 4}}>
              {
                steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;

                  return (
                    <Box
                      key={step.key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: isActive ? 'warning.lighter' : 'transparent',
                        transition: 'all 0.3s',
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: isActive ? 'warning.main' : isCompleted ? 'success.main' : 'grey.300',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <Icon sx={{fontSize: 24}} />
                      </Box>
                      <Typography
                        variant="body1"
                        color={isActive ? 'text.primary' : 'text.secondary'}
                        fontWeight={isActive ? 500 : 400}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  );
                })
              }
            </Stack>

            {/* Progress Bar */}
            <Box sx={{mb: 3}}>
              <LinearProgress
                variant="determinate"
                value={(currentStep / steps.length) * 100}
                sx={{
                  'height': 8,
                  'borderRadius': 4,
                  'bgcolor': 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: 'warning.main',
                  },
                }}
              />
            </Box>

            {/* Status Messages */}
            <Box textAlign="center">
              <Typography variant="body2" color="text.primary" sx={{mb: 1}}>
                • <Translate expression="caregiverInterview.Resume.Processing.Status" />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <Translate expression="caregiverInterview.Resume.Processing.TimeEstimate" />
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

FileLoadingSection.propTypes = {
  currentStep: PropTypes.number,
};

export default FileLoadingSection;

